import { useMemo, useState } from "react";
import { MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";
import { SKIN_CONDITIONS } from "../data/site";
import { Reveal, Eyebrow } from "./Reveal";

export const SkinConditions = () => {
  const [query, setQuery] = useState("");
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SKIN_CONDITIONS;
    return SKIN_CONDITIONS.filter(
      (c) => c.name.toLowerCase().includes(q) || c.desc.toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <section className="border-b border-border bg-background py-16 sm:py-20" data-testid="skin-conditions-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <Reveal>
            <Eyebrow>What we treat</Eyebrow>
            <h2 className="mt-4 max-w-xl font-display text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl">
              Start with your <em className="text-primary">concern.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative w-full md:w-80">
              <MagnifyingGlass size={17} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search a skin concern…"
                data-testid="conditions-search-input"
                className="w-full border border-border bg-background py-3 pl-11 pr-4 text-sm font-light outline-none transition-colors duration-300 placeholder:text-muted-foreground/70 focus:border-primary"
              />
            </div>
          </Reveal>
        </div>

        <p className="mt-6 text-xs uppercase tracking-[0.25em] text-muted-foreground" data-testid="conditions-count">
          {results.length} of {SKIN_CONDITIONS.length} conditions
        </p>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {results.map((c, i) => (
            <div key={c.name} className="group flex h-full flex-col border border-border bg-background p-7 transition-colors duration-500 hover:border-primary/40" data-testid={`condition-card-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <span className="font-display text-sm italic text-primary/50">{String(SKIN_CONDITIONS.indexOf(c) + 1).padStart(2, "0")}</span>
              <h3 className="mt-4 font-display text-2xl font-light tracking-tight transition-colors duration-300 group-hover:text-primary">
                {c.name}
              </h3>
              <p className="mt-2.5 flex-1 text-sm font-light leading-relaxed text-muted-foreground">{c.desc}</p>
              <a
                href={`#${c.treat}`}
                data-testid={`condition-link-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="mt-5 inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.25em] text-primary"
              >
                {c.treatLabel}
                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          ))}
        </div>

        {results.length === 0 && (
          <div className="border border-border bg-secondary/40 p-10 text-center" data-testid="conditions-empty-state">
            <p className="font-display text-2xl font-light italic">No match for "{query}"</p>
            <p className="mt-2 text-sm font-light text-muted-foreground">
              Every skin is unique — book a free consultation and we'll assess your concern in person.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};
